import {
  FormControl,
  FormGroup,
  AbstractControl,
  FormArray,
} from '@angular/forms';
import { Injectable } from '@angular/core';
import { FieldError } from '../models/form/FieldError';
import { getFieldErrorType } from './field-error-messages';

export interface InvalidFieldInfo {
    fieldId: string;
    errorType: string;
    value?: unknown;
}

/**
 * Percorre um FormGroup (incluindo sub-grupos) e retorna os campos inválidos e habilitados,
 * na ordem de declaração. Cada item traz apenas metadados (id + tipo de erro), sem PII.
 */
export function collectInvalidFields(
    formGroup: FormGroup,
    parentKey: string = '',
): InvalidFieldInfo[] {
    const result: InvalidFieldInfo[] = [];

    Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.get(key);
        if (!control || control.disabled) {
            return;
        }

        const fieldId = parentKey ? `${parentKey}.${key}` : key;

        if (control instanceof FormGroup) {
            result.push(...collectInvalidFields(control, fieldId));
            return;
        }

        if (control.invalid) {
            result.push({
                fieldId,
                errorType: getFieldErrorType(control) || 'invalid',
                value: control.value,
            });
        }
    });

    return result;
}

/**
 * Rola a tela até o primeiro campo obrigatório inválido e tenta focá-lo.
 * Best-effort: procura por `[formcontrolname]` ou `#id` correspondente.
 */
export function scrollToFirstInvalidField(formGroup: FormGroup): void {
    const invalid = collectInvalidFields(formGroup);
    if (!invalid.length || typeof document === 'undefined') {
        return;
    }

    const key = invalid[0].fieldId.split('.').pop();
    if (!key) {
        return;
    }

    const el = document.querySelector<HTMLElement>(
        `[formcontrolname="${key}"], #${key}`,
    );

    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function') {
            el.focus();
        }
    }
}

@Injectable()
export class FormsUtils {
  constructor() {
    /**/
  }

  public ValidFormBase(form: FormGroup, bringToView: boolean = true) {
    return new Promise<void>((resolve) => {
      if (form.valid) {
        form.markAsPending();
        try {
          resolve();
        } catch (err) {
          return;
        }
      } else this.validateFormGroup(form, bringToView);
    });
  }

  public ValidFormBaseSync(
    form: FormGroup,
    callback: () => any,
    bringToView: boolean = true,
    callbackInvalid: () => any = null
  ) {
    form = this.removeBackendErrors(form);
    form.updateValueAndValidity();

    if (form.valid) {
      try {
        form.markAsPending();
        callback();
      } catch (err) {
        throw new Error(err);
      }
    } else {
      this.validateFormGroup(form, bringToView);
      if (callbackInvalid) callbackInvalid();
    }
  }

  /**
   * Scrolla a página pro primeiro erro encontrado na lista
   * @param fieldsArray lista de erros de validação
   */
  public ValidateBackendScroll(fieldsArray: any) {
    const field = $(`#${fieldsArray[0].field}`);
    if (field.length) {
      $('html, body').animate(
        {
          scrollTop: field.offset().top - 50,
        },
        800
      );
    }
  }

  public ValidFormArray(form: FormGroup, callback: () => any) {
    let bringToView = true;
    if (form.valid) {
      callback();
    } else {
      Object.keys(form.controls).forEach((field) => {
        const control = form.get(field) as any;
        if (control instanceof FormControl) {
          control.markAsTouched({ onlySelf: true });
          if (!control.valid && bringToView) {
            $('html, body').animate(
              {
                scrollTop: $(`#${field}`).offset().top - 50,
              },
              800
            );
            $(`#${field}`).focus();
            bringToView = false;
          }
        } else {
          let i = 0;
          Object.keys(control.controls).forEach((arrayF) => {
            const controlArr = control.get(arrayF) as FormGroup;
            Object.keys(controlArr.controls).forEach((fieldFromGroup) => {
              const controlFinal = controlArr.get(fieldFromGroup);
              controlFinal.markAsTouched({ onlySelf: true });
              if (controlFinal.invalid && bringToView) {
                const fieldAnchor = $(`#${field}-${fieldFromGroup}-${i}`);
                $('html, body').animate(
                  {
                    scrollTop: $(fieldAnchor).offset().top - 50,
                  },
                  800
                );
                $(fieldAnchor).focus();
                bringToView = false;
              }
            });
            i++;
          });
        }
      });
    }
  }

  /**
   * Valida formulário que é um grupo de arrays.
   * @param form Formulário a ser validado.
   * @param callback Função de callback em caso de sucesso.
   */
  public ValidFormGroupOfArray(form: FormGroup, callback: () => any) {
    let bringToView = true;
    if (form.valid) {
      try {
        callback();
      } catch (err) {
        throw new Error(err);
      }
    } else {
      Object.keys(form.controls).forEach((field) => {
        const control = form.get(field) as any;
        if (control instanceof FormControl) {
          control.markAsTouched({ onlySelf: true });
          if (!control.valid && bringToView) {
            $('html, body').animate(
              {
                scrollTop: $(`#${field}`).offset().top - 50,
              },
              800
            );
            $(`#${field}`).focus();
            bringToView = false;
          }
        } else {
          let i = 0;
          Object.keys(control.controls).forEach((arrayF) => {
            const controlArr = control.get(arrayF) as FormArray;
            Object.keys(controlArr.controls).forEach((fieldFromGroup) => {
              const controlFinal = controlArr.get(fieldFromGroup);
              controlFinal.markAsTouched({ onlySelf: true });
              if (controlFinal.invalid && bringToView) {
                const fieldAnchor = $(`#${field}-${fieldFromGroup}-${i}`);
                $('html, body').animate(
                  {
                    scrollTop: $(fieldAnchor).offset().top - 50,
                  },
                  800
                );
                $(fieldAnchor).focus();
                bringToView = false;
              }
            });
            i++;
          });
        }
      });
    }
  }

  /**
   * Validador genérico de formulários.
   * @param form Formulário a ser validado.
   * @param callback Função de callback em caso de sucesso.
   * @author Daniel
   */
  public validForm(form: any, callback: () => any) {
    if (form.valid) {
      try {
        form.markAsPending();
        callback();
      } catch (err) {
        throw new Error(err);
      }
    } else {
      this.ValidFormGeneral(form, [], true);
      setTimeout(() => {
        const fieldAnchor = document.querySelector('.input-error');
        if ($(fieldAnchor).length) {
          $('html, body').animate(
            {
              scrollTop: $(fieldAnchor).offset().top - 50,
            },
            800
          );
          $(fieldAnchor).focus();
        }
      }, 200);
    }
  }

  /**
   * Retorna os campos inválidos de um formulário, marcando-os como touched
   * para que a UI exiba os erros. Percorre `FormGroup` e `FormArray`
   * recursivamente (necessário para telas com listas dinâmicas, como sócios
   * e representantes da estrutura societária).
   *
   * Devolve o `control` junto do nome (não só a chave) para que o chamador
   * consiga extrair a mensagem de erro real via `getFieldErrorMessage` —
   * sem isso, o único jeito de reportar o erro seria um texto fixo.
   *
   * @param form formulário (ou controle) a inspecionar
   * @param prefix nome do campo pai, usado apenas na recursão
   */
  public getInvalidFields(
    form: AbstractControl,
    prefix: string = ''
  ): Array<{ name: string; control: AbstractControl }> {
    const controls = (form as FormGroup | FormArray).controls;

    if (!controls) {
      form.markAsTouched({ onlySelf: true });
      return form.invalid ? [{ name: prefix, control: form }] : [];
    }

    return Object.keys(controls).reduce(
      (invalidFields: Array<{ name: string; control: AbstractControl }>, key: string) => {
        const control = controls[key];
        const fieldName = prefix ? `${prefix}.${key}` : key;
        return [...invalidFields, ...this.getInvalidFields(control, fieldName)];
      },
      []
    );
  }

  /**
   * Valida o formulário inteiro, faz scroll/foco até o primeiro campo
   * obrigatório inválido (via `validForm`) e entrega ao callback de erro a
   * lista dos campos que falharam (nome + control), sem nenhum valor de
   * campo — usado para os eventos `Field Error Displayed` do submit
   * rejeitado.
   *
   * @param form formulário a validar
   * @param onValid callback disparado quando o formulário é válido
   * @param onInvalid callback disparado com os campos inválidos
   * @returns se o formulário estava válido antes de `validForm` marcá-lo como pendente
   */
  public validFormTracked(
    form: FormGroup,
    onValid: () => any,
    onInvalid: (invalidFields: Array<{ name: string; control: AbstractControl }>) => any
  ): boolean {
    form.updateValueAndValidity({ emitEvent: false });
    const { valid, invalid } = form;
    this.validForm(form, onValid);

    if (invalid) {
      onInvalid(this.getInvalidFields(form));
    }

    return valid;
  }

  public getControlName(control: AbstractControl) {
    let controlName = null;
    const parent = control.parent;

    // only such parent, which is FormGroup, has a dictionary
    // with control-names as a key and a form-control as a value
    if (parent instanceof FormGroup) {
      // now we will iterate those keys (i.e. names of controls)
      Object.keys(parent.controls).forEach((name) => {
        // and compare the passed control and
        // a child control of a parent - with provided name (we iterate them all)
        if (control === parent.controls[name]) {
          // both are same: control passed to Validator
          //  and this child - are the same references
          controlName = name;
        }
      });
    }
    // we either found a name or simply return null
    return controlName;
  }

  /**
   * Limpa os controles de um formArray sem quebra qualquer subscription que nele exista.
   * @param formArray formArray a ser limpado
   */
  public clearFormArray = (formArray: FormArray) => {
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  };
  /**
   * Extende o FormGroup com métodos customizados
   */
  public extendFormGroupPrototype() {
    (FormGroup.prototype as any).validFields = function (err: FieldError[]) {
      err.forEach((e) => {
        const field: FormControl = this.get(e.field) as FormControl;
        if (field) field.setErrors({ backendError: { message: e.message } });
      });
    };
  }

  /**
   * Validador recursivo de formulário, idependente do tipo.
   * @param form Formulário a ser validado
   * @param errors Campos com erros - acumulador
   * @param pristine Flag pra verificar se é a primeira vez que a função é executada
   * @author Daniel
   */
  private ValidFormGeneral(form: any, errors: any[], pristine: boolean) {
    const controls = form.controls;
    if (!controls) {
      form.markAsTouched({ onlySelf: true });
      if (form.invalid) {
        errors.push(form);
      }
    }
    for (const item in controls) {
      if (controls.hasOwnProperty(item)) {
        const formItem = form.controls[item];
        if (formItem) {
          if (!formItem.controls) {
            formItem.markAsTouched({ onlySelf: true });
            if (formItem.invalid) {
              errors.push(formItem);
            }
          } else {
            this.ValidFormGeneral(formItem, errors, false);
          }
        }
      }
    }

    return errors;
  }

  /**
   * Remover os erro de backend
   * @param form
   */
  private removeBackendErrors(formControl) {
    if (formControl instanceof FormControl) {
      const errors = {
        ...{},
        ...formControl.errors,
        ...{ backendError: null },
      };
      formControl.setErrors(errors);
      formControl.updateValueAndValidity();
      return formControl;
    }

    Object.keys(formControl.controls).forEach((field) => {
      this.removeBackendErrors(formControl.get(field));
    });
    return formControl;
  }

  /**
   * Realiza a verificação dos controls de um formGroup
   * @param form formulário a ser validado
   * @param bringToview realizar scroll da tela até o controle
   */
  private validateFormGroup(form: FormGroup, bringToView: boolean) {
    if (form.status.equals('PENDING')) return;

    for (const field in form.controls) {
      if (form.controls.hasOwnProperty(field)) {
        const control = form.get(field);
        if (!this.proccessForm(control, bringToView, field)) {
          bringToView = false;
        }
      }
    }
  }

  /** Processa um formControl (FormGroup ou FormControl) */
  private proccessForm(control: any, bringToView: boolean, field: string) {
    if (control instanceof FormGroup)
      return this.validateFormGroup(control, bringToView);

    return this.validateControls(control, bringToView, field);
  }
  /**
   * Realiza a verificação de validade de um control
   * @param control controle a ser validado
   * @param bringToview realizar scroll da tela até o controle
   * @param field nome do campo (referência DOM via id)
   */
  private validateControls(
    control: AbstractControl,
    bringToView: boolean,
    field: string
  ) {
    control.markAsTouched({ onlySelf: true });
    // control.updateValueAndValidity();
    if (!control.valid && bringToView) {
      if ($(`#${field}`).length) {
        $('html, body').animate(
          {
            scrollTop: $(`#${field}`).offset().top - 50,
          },
          800
        );
      } else {
        $('html, body').animate({ scrollTop: 0 });
        // tslint:disable-next-line:no-console
        console.warn(`Campo não encontrado para realizar o scroll: ${field}`);
      }

      $(`#${field}`).focus();
      return false;
    }

    return true;
  }
}
